import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FlaskConical, ArrowLeft, Lightbulb, Target, TrendingUp, FileText, ExternalLink, Send, User, RefreshCw, Info } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';
import { Footer } from '@/components/Footer';
import VisibilityScoreBreakdown from '@/components/VisibilityScoreBreakdown';
import MethodologyScoreBreakdown from '@/components/MethodologyScoreBreakdown';
import ClinicalValueScore from '@/components/ClinicalValueScore';
import DataIntegrityPanel from '@/components/DataIntegrityPanel';
import ScoreHistory from '@/components/ScoreHistory';
import { EvidenceSynthesisPanel } from '@/components/article/EvidenceSynthesisPanel';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import GraphicalAbstract from '@/components/GraphicalAbstract';
import TypeAwareEvaluationPanel from '@/components/TypeAwareEvaluationPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVIDENCE_SYNTHESIS_TYPES = ['Meta-analysis', 'Systematic review', 'Scoping review', 'Narrative review', 'Critical appraisal review'];

const ResearchInsightPage = () => {
  const { articleId } = useParams();
  const [insight, setInsight] = useState(null);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regeneratingTopics, setRegeneratingTopics] = useState(false);
  const [isAuthorLoggedIn, setIsAuthorLoggedIn] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [pdfInfo, setPdfInfo] = useState({ has_pdf: false, download_count: 0 });
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Helper function to format values - replace "not_reported" with friendly message
  const formatValue = (value) => {
    if (!value || value === 'not_reported' || value === 'not reported') {
      return (
        <span className="text-slate-500 italic flex items-center gap-2">
          <span className="text-blue-500">ⓘ</span>
          Information was not available in the analyzed publication
        </span>
      );
    }
    return value;
  };

  useEffect(() => {
    loadData();
    loadPdfInfo();
    // Check if author is logged in
    const authorAuth = sessionStorage.getItem('author_authenticated');
    setIsAuthorLoggedIn(!!authorAuth);
    // Check if admin is logged in
    const adminAuth = sessionStorage.getItem('admin_authenticated');
    setIsAdmin(!!adminAuth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [insightData, articleData] = await Promise.all([
        api.getResearchInsight(articleId),
        api.getArticleDetail(articleId)
      ]);
      setInsight(insightData);
      setArticle(articleData);
      
      // Fetch DOI metrics if DOI exists
      if (articleData.doi) {
        fetchDOIMetrics(articleData.doi);
      }
    } catch (error) {
      toast.error('Failed to load research insight');
    }
    setLoading(false);
  };
  
  const fetchDOIMetrics = async (doi) => {
    setLoadingMetrics(true);
    try {
      const response = await fetch(`${API_URL}/api/doi-metrics/${encodeURIComponent(doi)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update article with visibility metrics
          setArticle(prev => ({
            ...prev,
            visibility_metrics: {
              citations: data.metrics.total_citations,
              influential_citations: data.metrics.influential_citations,
              references: data.metrics.references,
              altmetric_score: data.metrics.altmetric_score,
              social_mentions: data.metrics.social_mentions,
              sources: data.metrics.sources,
              fetched_at: new Date().toISOString()
            }
          }));
        }
      }
    } catch (error) {
      void 0;
    }
    setLoadingMetrics(false);
  };

  const loadPdfInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/articles/${articleId}/pdf-info`);
      if (response.ok) {
        const data = await response.json();
        setPdfInfo(data);
      }
    } catch (error) {
      void 0;
    }
  };

  const handleReanalyze = async () => {
    setReanalyzing(true);
    try {
      toast.info('Re-analyzing with full text. This may take 30-60 seconds...');
      await api.reanalyzeArticle(articleId);
      toast.success('Re-analysis complete! Reloading data...');
      await loadData();
    } catch (error) {
      toast.error('Re-analysis failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setReanalyzing(false);
    }
  };

  
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await fetch(`${API_URL}/api/articles/${articleId}/download-pdf`);
      
      if (!response.ok) {
        throw new Error('PDF is not available for download');
      }
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'article.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF downloaded successfully!');
      loadPdfInfo();
    } catch (error) {
      toast.error(error.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleRegenerateTopics = async () => {
    setRegeneratingTopics(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/insight/${articleId}/regenerate-topics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error('Failed to regenerate topics');
      
      const data = await response.json();
      
      // Update insight with new topics
      setInsight(prev => ({
        ...prev,
        future_research_directions: data.new_topics
      }));
      
      toast.success('New research topics generated!');
    } catch (error) {
      toast.error('Failed to generate new topics: ' + error.message);
    }
    setRegeneratingTopics(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-600">Loading research insight...</p>
      </div>
    );
  }

  if (!insight) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-slate-600 mb-4">No research insight found for this article.</p>
            <Link to="/admin">
              <Button>Back to Admin</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getImpactColor = (type) => {
    switch (type) {
      case 'practice-changing': return 'bg-green-100 text-green-800';
      case 'confirmatory': return 'bg-blue-100 text-blue-800';
      case 'hypothesis-generating': return 'bg-purple-100 text-purple-800';
      case 'methodological': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEvidenceLevelColor = (level) => {
    switch (level) {
      case 'Level I': return 'bg-green-100 text-green-800';
      case 'Level II': return 'bg-blue-100 text-blue-800';
      case 'Level III': return 'bg-yellow-100 text-yellow-800';
      case 'Level IV': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      {article && (
        <PageSEO
          title={insight?.seo_metadata?.seo_title ? `${insight.seo_metadata.seo_title} | Evidentica` : `${article.title} | Research Analysis | Evidentica`}
          description={insight?.seo_metadata?.meta_description || article.abstract?.substring(0, 155) || `Comprehensive AI-powered analysis of: ${article.title}. Evidence level, methodology score, and impact assessment.`}
          keywords={insight?.seo_metadata?.secondary_keywords?.join(', ') || `${article.title}, ${article.journal || 'medical research'}, ${insight?.research_impact_type || 'evidence-based medicine'}, clinical study, research analysis`}
          canonical={`https://evidentica.net/research-insight/${articleId}`}
          article={true}
          publishedTime={article.created_at}
          modifiedTime={article.updated_at}
        />
      )}
      <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center space-x-2">
              <FlaskConical className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
              <span className="text-lg sm:text-xl font-bold text-slate-900">Research Insight</span>
            </Link>
            <div className="flex items-center gap-1 sm:gap-2">
              <Link to="/explorer">
                <Button variant="ghost" size="sm" data-testid="back-to-explorer-btn">
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  <span className="hidden sm:inline">Back to Explorer</span>
                  <span className="sm:hidden">Explorer</span>
                </Button>
              </Link>
              {isAuthorLoggedIn && (
                <Link to="/author/dashboard">
                  <Button variant="ghost" size="sm" data-testid="nav-author-dashboard-btn">
                    <User className="mr-1 h-4 w-4" />
                    <span className="hidden sm:inline">Dashboard</span>
                  </Button>
                </Link>
              )}
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-testid="insight-title">
            {insight.seo_insight_title || article?.title}
          </h1>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-wrap gap-2">
              {(() => {
                const ie = insight.impact_estimation || {};
                const category = ie.research_impact_category 
                  || (ie.clinical_relevance_level 
                    ? ie.clinical_relevance_level.charAt(0).toUpperCase() + ie.clinical_relevance_level.slice(1)
                    : null);
                if (!category) return null;
                const colorClass = category === 'High' ? 'bg-green-100 text-green-800' 
                  : category === 'Moderate' ? 'bg-yellow-100 text-yellow-800' 
                  : 'bg-blue-100 text-blue-800';
                return (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge className={`${colorClass} text-sm sm:text-base py-1 px-2 sm:px-3 cursor-help`} data-testid="impact-type-badge">
                          Clinical Impact: {category}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                        <p><strong>High</strong> — Directly affects treatment decisions or guidelines.</p>
                        <p><strong>Moderate</strong> — Meaningful evidence, requires further validation.</p>
                        <p><strong>Low</strong> — Preliminary or hypothesis-generating.</p>
                        {insight.research_impact_type && (
                          <p className="mt-1 text-slate-400">Research type: {insight.research_impact_type.replace('-', ' ')}</p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })()}
              {article?.methodology_score != null && (
                <Badge variant="outline" className="text-sm sm:text-base py-1 px-2 sm:px-3" data-testid="methodology-score-badge">
                  {(() => {
                    const tae = insight?.type_aware_evaluation?.type_specific_score;
                    const hasRange = tae?.score_min != null && tae?.score_max != null && tae.score_min !== tae.score_max;
                    const scoreLabel = EVIDENCE_SYNTHESIS_TYPES.includes(article.normalized_study_type)
                      ? 'ESS' : article.normalized_study_type === 'Case report' || article.normalized_study_type === 'Case series'
                      ? 'CLV' : 'Score';
                    const maxScore = EVIDENCE_SYNTHESIS_TYPES.includes(article.normalized_study_type) ? 35
                      : article.normalized_study_type === 'Case report' || article.normalized_study_type === 'Case series' ? 35 : 70;
                    if (hasRange) {
                      return `${scoreLabel}: ${tae.score_min}–${tae.score_max}/${maxScore}`;
                    }
                    return `${scoreLabel}: ${article.methodology_score}/${maxScore}`;
                  })()}
                </Badge>
              )}
            </div>
            
            {/* PDF Download & Re-analyze Section */}
            <div className="w-full sm:w-auto mt-2 sm:mt-0">
              <div className="flex items-center gap-2 flex-wrap">
                <GraphicalAbstract articleId={articleId} />
                {pdfInfo.has_pdf ? (
                  <>
                    <Button
                      onClick={handleDownloadPdf}
                      disabled={downloadingPdf}
                      className="bg-blue-600 hover:bg-blue-700 text-sm"
                      data-testid="download-pdf-btn"
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      {downloadingPdf ? 'Downloading...' : 'Download PDF'}
                    </Button>
                    {isAdmin && (
                      <Button
                        onClick={handleReanalyze}
                        disabled={reanalyzing}
                        variant="outline"
                        className="text-orange-600 border-orange-300 hover:bg-orange-50 text-sm"
                        data-testid="reanalyze-btn"
                      >
                        <RefreshCw className={`mr-2 h-4 w-4 ${reanalyzing ? 'animate-spin' : ''}`} />
                        {reanalyzing ? 'Re-analyzing...' : 'Re-analyze with PDF'}
                      </Button>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-slate-500 italic text-center sm:text-left">
                    No available PDF article
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Evidence Snapshot - Quick 10-second understanding */}
        <Card className="mb-6 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-blue-200" data-testid="evidence-snapshot-card">
          <CardHeader>
            <CardTitle className="text-blue-900 flex items-center">
              <FlaskConical className="mr-2 h-5 w-5" />
              Evidence Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <p className="text-sm text-slate-600 mb-1">Study design</p>
                <p className="font-semibold text-slate-900">{article?.normalized_study_type || 'Not reported'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1">Sample size</p>
                <p className="font-semibold text-slate-900">{article?.sample_size || 'Not reported'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1">Follow-up</p>
                <p className="font-semibold text-slate-900">
                  {article?.follow_up_duration_months ? `${article.follow_up_duration_months} months` : 'Not reported'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1">Evidence level</p>
                <Badge className={getEvidenceLevelColor(article?.evidence_level)}>
                  {article?.evidence_level || 'Level IV'}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1 flex items-center gap-1">
                  Clinical impact
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                        <p className="font-semibold mb-1">How Clinical Impact is assessed:</p>
                        <p><strong>High</strong> — Results directly affect treatment decisions or clinical guidelines.</p>
                        <p><strong>Moderate</strong> — Findings contribute meaningful evidence but require further validation.</p>
                        <p><strong>Low</strong> — Preliminary or hypothesis-generating observations with limited direct applicability.</p>
                        <p className="mt-1 text-slate-400">Determined by AI analysis of the study's clinical relevance, innovation level, and potential to change practice.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </p>
                {(() => {
                  const category = insight.impact_estimation?.research_impact_category 
                    || (insight.impact_estimation?.clinical_relevance_level 
                      ? insight.impact_estimation.clinical_relevance_level.charAt(0).toUpperCase() + insight.impact_estimation.clinical_relevance_level.slice(1)
                      : null);
                  return (
                    <Badge className={category ? 
                      (category === 'High' ? 'bg-green-600' : 
                       category === 'Moderate' ? 'bg-yellow-600' : 'bg-blue-600') : 'bg-gray-600'}>
                      {category || 'Not assessed'}
                    </Badge>
                  );
                })()}
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1">
                  {article?.normalized_study_type === 'Case report' || article?.normalized_study_type === 'Case series' 
                    ? 'Clinical value' 
                    : EVIDENCE_SYNTHESIS_TYPES.includes(article?.normalized_study_type)
                      ? 'Evidence Synthesis Score'
                      : 'Methodology score'}
                </p>
                <p className="font-semibold text-blue-900 text-lg">
                  {(() => {
                    const tae = insight?.type_aware_evaluation?.type_specific_score;
                    const hasRange = tae?.score_min != null && tae?.score_max != null && tae.score_min !== tae.score_max;
                    const maxScore = (article?.normalized_study_type === 'Case report' || article?.normalized_study_type === 'Case series' || EVIDENCE_SYNTHESIS_TYPES.includes(article?.normalized_study_type)) ? 35 : 70;
                    if (article?.normalized_study_type === 'Case report' || article?.normalized_study_type === 'Case series') return 'See below';
                    if (hasRange) return `${tae.score_min}–${tae.score_max}/${maxScore}`;
                    return `${article?.methodology_score || 0}/${maxScore}`;
                  })()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Insight */}
        {insight.key_research_insight && (
          <Card className="mb-6 bg-blue-50 border-blue-200" data-testid="key-insight-card">
            <CardHeader>
              <CardTitle className="flex items-center text-blue-900">
                <Lightbulb className="mr-2 h-6 w-6" />
                Key Research Insight
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg text-blue-900">{insight.key_research_insight}</p>
            </CardContent>
          </Card>
        )}

        {/* Tabs for different sections */}
        <Tabs defaultValue="overview" className="mb-8">
          <div className="overflow-x-auto">
            <TabsList className="inline-flex w-auto min-w-full md:grid md:w-full md:grid-cols-5">
              <TabsTrigger value="overview" data-testid="tab-overview" className="whitespace-nowrap">Overview</TabsTrigger>
              <TabsTrigger value="scientific" data-testid="tab-scientific" className="whitespace-nowrap">Scientific Idea</TabsTrigger>
              <TabsTrigger value="methodology" data-testid="tab-methodology" className="whitespace-nowrap">Methodology</TabsTrigger>
              <TabsTrigger value="pico" data-testid="tab-pico" className="whitespace-nowrap">PICO</TabsTrigger>
              <TabsTrigger value="gaps" data-testid="tab-gaps" className="whitespace-nowrap">Research Gaps</TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Research Visibility Metrics - Show if data available OR if article has references */}
            {(article?.visibility_metrics || (article?.references && article.references.length > 0)) && (
              <Card data-testid="visibility-metrics-card" className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-purple-900">Research Visibility Metrics</CardTitle>
                    {article.doi && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => fetchDOIMetrics(article.doi)}
                        disabled={loadingMetrics}
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loadingMetrics ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Citation Metrics */}
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                        <TrendingUp className="mr-2 h-5 w-5 text-purple-600" />
                        Citation Metrics
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between py-2 border-b border-purple-200">
                          <span className="text-slate-600">Total Citations</span>
                          <span className="font-semibold text-purple-900">
                            {article.visibility_metrics?.citations || 0}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-purple-200">
                          <span className="text-slate-600">Influential Citations</span>
                          <span className="font-semibold text-purple-900">
                            {article.visibility_metrics?.influential_citations || 0}
                          </span>
                        </div>
                        {/* References metric removed per user request */}
                      </div>
                    </div>

                    {/* Social Impact */}
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                        <ExternalLink className="mr-2 h-5 w-5 text-purple-600" />
                        Social Impact
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between py-2 border-b border-purple-200">
                          <span className="text-slate-600">Altmetric Score</span>
                          <span className="font-semibold text-purple-900">
                            {article.visibility_metrics.altmetric_score || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-purple-200">
                          <span className="text-slate-600">Social Mentions</span>
                          <span className="font-semibold text-purple-900">{article.visibility_metrics.social_mentions || 0}</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-slate-600">Data Sources</span>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {(article.visibility_metrics.sources || []).map((source, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">{source}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-4 flex items-center gap-2">
                    <span>📊</span>
                    <span>Last updated: {new Date(article.visibility_metrics?.fetched_at || Date.now()).toLocaleString()}</span>
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Score Breakdowns - Conditional based on study type */}
            {article && (
              <>
                {article.normalized_study_type === 'Case report' || article.normalized_study_type === 'Case series' ? (
                  // Show Clinical Value Score for Case Reports/Series (no Visibility Score on this page)
                  <div className="space-y-4">
                    <ClinicalValueScore 
                      articleId={article.id} 
                      studyType={article.normalized_study_type}
                    />
                  </div>
                ) : EVIDENCE_SYNTHESIS_TYPES.includes(article.normalized_study_type) ? (
                  // Show ESS breakdown for Evidence Synthesis articles
                  <div className="space-y-4">
                    <EvidenceSynthesisPanel articleId={article.id} />
                    <Accordion type="multiple" className="space-y-4" defaultValue={['visibility']}>
                      <AccordionItem value="visibility" className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-purple-600" />
                            <span className="text-lg font-semibold text-slate-900">Visibility Score Breakdown</span>
                            <Badge variant="outline" className="ml-2 bg-purple-50 text-purple-700 border-purple-300">
                              {article.visibility_score || 0}/30 pts
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4">
                          <VisibilityScoreBreakdown articleId={article.id} />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                ) : (
                  // Show Methodology Score Breakdown for regular studies
                  <Accordion type="multiple" className="space-y-4" defaultValue={['visibility', 'methodology']}>
                    <AccordionItem value="visibility" className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-purple-600" />
                          <span className="text-lg font-semibold text-slate-900">Visibility Score Breakdown</span>
                          <Badge variant="outline" className="ml-2 bg-purple-50 text-purple-700 border-purple-300">
                            {article.visibility_score || 0}/30 pts
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <VisibilityScoreBreakdown articleId={article.id} />
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="methodology" className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <FlaskConical className="w-5 h-5 text-blue-600" />
                          <span className="text-lg font-semibold text-slate-900">Methodology Score Breakdown</span>
                          <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-300">
                            {article.methodology_score || 0}/70 pts
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <MethodologyScoreBreakdown articleId={article.id} />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              </>
            )}

            {/* Data Integrity Assessment */}
            {insight.data_integrity_assessment && (
              <div className="mt-6">
                <DataIntegrityPanel dataIntegrity={insight.data_integrity_assessment} />
              </div>
            )}

            {/* Type-Aware Evaluation (v2 Framework) */}
            {insight.type_aware_evaluation && (
              <div className="mt-6" data-testid="type-aware-section">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-blue-600" />
                  Type-Aware Research Evaluation
                </h2>
                <TypeAwareEvaluationPanel evaluation={insight.type_aware_evaluation} insight={insight} />
              </div>
            )}

            {/* Score History */}
            {article && (
              <div className="mt-6">
                <ScoreHistory articleId={article.id} />
              </div>
            )}

            {/* Study Overview */}
            {insight.study_overview && (
              <Card data-testid="study-overview-card">
                <CardHeader>
                  <CardTitle>Study Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">{insight.study_overview}</p>
                </CardContent>
              </Card>
            )}

            {/* Key Numerical Results */}
            {insight.key_numerical_results && insight.key_numerical_results.length > 0 && (
              <Card data-testid="numerical-results-card">
                <CardHeader>
                  <CardTitle>Key Numerical Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {insight.key_numerical_results.map((result, index) => (
                      <li key={`item-${index}-${typeof item === "string" ? item.slice(0,20) : index}`} className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>{result}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Key Takeaway */}
            {insight.key_takeaway && (
              <Card className="bg-green-50 border-green-200" data-testid="key-takeaway-card">
                <CardHeader>
                  <CardTitle className="text-green-900">Key Takeaway</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg text-green-900 font-medium">{insight.key_takeaway}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Scientific Idea Tab */}
          <TabsContent value="scientific" className="space-y-6">
            {insight.core_scientific_idea && (
              <Card data-testid="core-idea-card">
                <CardHeader>
                  <CardTitle>Core Scientific Idea</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">{insight.core_scientific_idea}</p>
                </CardContent>
              </Card>
            )}

            {insight.problem_addressed && (
              <Card data-testid="problem-card">
                <CardHeader>
                  <CardTitle>Problem Addressed</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">{insight.problem_addressed}</p>
                </CardContent>
              </Card>
            )}

            {insight.evidence_contribution && (
              <Card data-testid="evidence-card">
                <CardHeader>
                  <CardTitle>Evidence Contribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge variant="outline" className="text-base py-1 px-3">
                    {insight.evidence_contribution}
                  </Badge>
                </CardContent>
              </Card>
            )}

            {insight.clinical_translation && (
              <Card data-testid="clinical-translation-card">
                <CardHeader>
                  <CardTitle>Clinical Translation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">{insight.clinical_translation}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Methodology Tab */}
          <TabsContent value="methodology" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Strengths */}
              {insight.methodological_strengths && insight.methodological_strengths.length > 0 && (
                <Card data-testid="strengths-card">
                  <CardHeader>
                    <CardTitle className="text-green-700">Methodological Strengths</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {insight.methodological_strengths.map((strength, index) => (
                        <li key={`item-${index}-${typeof item === "string" ? item.slice(0,20) : index}`} className="flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          <span>{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Limitations */}
              {insight.methodological_limitations && insight.methodological_limitations.length > 0 && (
                <Card data-testid="limitations-card">
                  <CardHeader>
                    <CardTitle className="text-amber-700">Methodological Limitations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {insight.methodological_limitations.map((limitation, index) => (
                        <li key={`item-${index}-${typeof item === "string" ? item.slice(0,20) : index}`} className="flex items-start">
                          <span className="text-amber-600 mr-2">⚠</span>
                          <span>{limitation}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Structured Methodology */}
            {insight.structured_methodology && (
              <Card data-testid="structured-methodology-card">
                <CardHeader>
                  <CardTitle>Study Characteristics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(insight.structured_methodology).map(([key, value]) => (
                      value && value !== 'not_reported' && (
                        <div key={key}>
                          <p className="text-sm text-slate-600">{key.replace(/_/g, ' ')}</p>
                          <p className="font-medium">{value}</p>
                        </div>
                      )
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Subgroups with Limited Evidence */}
            {insight.subgroups_limited_evidence && insight.subgroups_limited_evidence.length > 0 && (
              <Card className="border-amber-200" data-testid="subgroups-card">
                <CardHeader>
                  <CardTitle className="text-amber-900">Subgroups With Limited Evidence</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {insight.subgroups_limited_evidence.map((subgroup, index) => (
                      <li key={`item-${index}-${typeof item === "string" ? item.slice(0,20) : index}`} className="flex items-start">
                        <span className="text-amber-600 mr-2">⚠</span>
                        <span>{subgroup}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Guideline Relevance */}
            <Card data-testid="guideline-relevance-card" className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50">
              <CardHeader>
                <CardTitle className="text-indigo-900">Guideline Relevance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  Assessment of this study's potential impact on clinical practice guidelines
                </p>
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-green-900">High Relevance</span>
                      <Badge className="bg-green-600">Grade A Evidence</Badge>
                    </div>
                    <p className="text-sm text-green-800 mt-1">
                      RCTs with large sample sizes, meta-analyses - likely to influence guideline recommendations
                    </p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-blue-900">Potential Relevance</span>
                      <Badge className="bg-blue-600">Grade B Evidence</Badge>
                    </div>
                    <p className="text-sm text-blue-800 mt-1">
                      Well-designed cohort studies, smaller RCTs - may inform specific guideline aspects
                    </p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-yellow-900">Moderate Relevance</span>
                      <Badge className="bg-yellow-600">Grade C Evidence</Badge>
                    </div>
                    <p className="text-sm text-yellow-800 mt-1">
                      Case-control studies, retrospective cohorts - may support existing recommendations
                    </p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">Exploratory Evidence</span>
                      <Badge className="bg-gray-600">Grade D Evidence</Badge>
                    </div>
                    <p className="text-sm text-gray-800 mt-1">
                      Case series, case reports - hypothesis-generating, not guideline-forming
                    </p>
                  </div>
                </div>
                
                {/* Current article assessment */}
                <div className="mt-4 p-4 bg-white rounded-lg border-2 border-indigo-300">
                  <p className="font-semibold text-indigo-900 mb-2">This Study:</p>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      article?.evidence_level === 'Level I' ? 'bg-green-600' :
                      article?.evidence_level === 'Level II' ? 'bg-blue-600' :
                      article?.evidence_level === 'Level III' ? 'bg-yellow-600' : 'bg-gray-600'
                    }>
                      {article?.evidence_level || 'Level IV'}
                    </Badge>
                    <span className="text-sm text-slate-700">
                      {article?.evidence_level === 'Level I' && '→ High guideline relevance'}
                      {article?.evidence_level === 'Level II' && '→ Potential guideline relevance'}
                      {article?.evidence_level === 'Level III' && '→ Moderate guideline relevance'}
                      {article?.evidence_level === 'Level IV' && '→ Exploratory evidence'}
                      {!article?.evidence_level && '→ Not assessed'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PICO Tab */}
          <TabsContent value="pico" className="space-y-6">
            {insight.pico && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card data-testid="pico-population-card">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Target className="mr-2 h-5 w-5 text-blue-600" />
                      Population
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{formatValue(insight.pico.population)}</p>
                  </CardContent>
                </Card>

                <Card data-testid="pico-intervention-card">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <TrendingUp className="mr-2 h-5 w-5 text-green-600" />
                      Intervention
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{formatValue(insight.pico.intervention)}</p>
                  </CardContent>
                </Card>

                <Card data-testid="pico-comparator-card">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="mr-2 h-5 w-5 text-purple-600" />
                      Comparator
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{formatValue(insight.pico.comparator)}</p>
                  </CardContent>
                </Card>

                <Card data-testid="pico-outcome-card">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Target className="mr-2 h-5 w-5 text-amber-600" />
                      Outcome
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{formatValue(insight.pico.outcome)}</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Research Gaps Tab */}
          <TabsContent value="gaps" className="space-y-6">
            {insight.research_gaps && insight.research_gaps.length > 0 && (
              <Card data-testid="research-gaps-card">
                <CardHeader>
                  <CardTitle>Identified Research Gaps</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {insight.research_gaps.map((gap, index) => (
                      <li key={`item-${index}-${typeof item === "string" ? item.slice(0,20) : index}`} className="flex items-start p-3 bg-slate-50 rounded">
                        <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                          {index + 1}
                        </span>
                        <span>{gap}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {insight.future_research_directions && (
              <Card className="bg-purple-50 border-purple-200" data-testid="future-research-card">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-purple-900">Future Research Directions</CardTitle>
                    <Button
                      size="sm"
                      onClick={handleRegenerateTopics}
                      disabled={regeneratingTopics}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {regeneratingTopics ? (
                        <>
                          <span className="animate-spin mr-2">↻</span>
                          Generating...
                        </>
                      ) : (
                        <>
                          <Lightbulb className="h-4 w-4 mr-2" />
                          Generate New Topics
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Display as suggested topics */}
                    <div className="text-purple-900 whitespace-pre-line">
                      {formatValue(insight.future_research_directions)}
                    </div>
                    
                    {/* Journal Submission Buttons */}
                    <div className="border-t border-purple-300 pt-4 mt-4">
                      <p className="text-sm font-semibold text-purple-900 mb-3">
                        Submit Research Topics to Our Journals:
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button
                          variant="outline"
                          className="bg-white hover:bg-purple-100 border-purple-300 text-purple-700 flex items-center justify-center"
                          onClick={() => window.open('https://www.mdpub.net/', '_blank')}
                          data-testid="submit-mdpub-btn"
                        >
                          <Send className="mr-2 h-4 w-4" />
                          Submit to MDPub.net
                          <ExternalLink className="ml-2 h-3 w-3" />
                        </Button>
                        
                        <Button
                          variant="outline"
                          className="bg-white hover:bg-purple-100 border-purple-300 text-purple-700 flex items-center justify-center"
                          onClick={() => window.open('https://www.ejos.org/', '_blank')}
                          data-testid="submit-ejos-btn"
                        >
                          <Send className="mr-2 h-4 w-4" />
                          Submit to EJOS.org
                          <ExternalLink className="ml-2 h-3 w-3" />
                        </Button>
                      </div>
                      <p className="text-xs text-purple-700 mt-3">
                        These research directions can inspire new studies. Submit your proposals to our partner journals for publication consideration.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* How to Cite This Article */}
        {insight.citation_examples && (
          <Card className="mb-8 border-blue-200" data-testid="citation-card">
            <CardHeader>
              <CardTitle className="text-blue-900">How to Cite This Article</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {insight.citation_examples.apa && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">APA 7th Edition</p>
                    <p className="text-sm bg-slate-50 p-3 rounded border border-slate-200 font-mono">
                      {insight.citation_examples.apa}
                    </p>
                  </div>
                )}
                {insight.citation_examples.mla && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">MLA 9th Edition</p>
                    <p className="text-sm bg-slate-50 p-3 rounded border border-slate-200 font-mono">
                      {insight.citation_examples.mla}
                    </p>
                  </div>
                )}
                {insight.citation_examples.chicago && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Chicago 17th Edition</p>
                    <p className="text-sm bg-slate-50 p-3 rounded border border-slate-200 font-mono">
                      {insight.citation_examples.chicago}
                    </p>
                  </div>
                )}
                {insight.citation_examples.vancouver && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Vancouver Style</p>
                    <p className="text-sm bg-slate-50 p-3 rounded border border-slate-200 font-mono">
                      {insight.citation_examples.vancouver}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analysis Metadata */}
        <Card className="bg-slate-100">
          <CardContent className="pt-6">
            <div className="text-sm text-slate-600">
              <p>Analysis Version: {insight.analysis_version}</p>
              <p>Analyzed: {new Date(insight.analysis_date).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
      </div>
    </>
  );
};

export default ResearchInsightPage;
